import {useEffect, useMemo, useRef, useState} from 'react'
import moment from 'moment'
import Select from 'react-select'
import {useDispatch, useSelector} from 'react-redux'
import {useDebounce} from '../../../../../../_metronic/helpers'
import {GetCurrentData} from '../../../../../Utils/_helpers/GetCurrentData'
import {
  GetPrevioslySentCampaigsList,
  GetSavedCampaigsList,
  getAllPreviouslySendCampaignsList,
  getAllSavedCampaignsList,
} from '../../BulkMessageSlice/BulkMessageSlice'

const formatDateLabel = (date: string) => {
  return date ? moment(date).format('DD/MM/YYYY') : ''
}

const createOption = (item: any, source: string) => ({
  value: item?._id,
  label: `${item?.name || 'Untitled'} - ${formatDateLabel(item?.createdAt)}`,
  source,
  data: item,
})

const CampaignSearchSelect = ({value, onChange, className = '', placeholder = 'Select saved campaign'}: any) => {
  const dispatch = useDispatch<any>()
  const currentUser = GetCurrentData()
  const savedCampaigns = useSelector(getAllSavedCampaignsList)
  const previousCampaigns = useSelector(getAllPreviouslySendCampaignsList)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [savedCampaignSearchResults, setSavedCampaignSearchResults] = useState<any>({})
  const [previousCampaignSearchResults, setPreviousCampaignSearchResults] = useState<any>({})
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const hasSearchTerm = Boolean(debouncedSearchTerm?.trim())
  const requestSequenceRef = useRef(0)

  const resetSearchTerm = () => {
    setSearchTerm('')
    setSearchError('')
    setSavedCampaignSearchResults({})
    setPreviousCampaignSearchResults({})
  }

  const options = useMemo(() => {
    const nextOptions = []
    const activeSavedCampaigns = hasSearchTerm ? savedCampaignSearchResults : savedCampaigns
    const activePreviousCampaigns = hasSearchTerm
      ? previousCampaignSearchResults
      : previousCampaigns
    const previousSms = activePreviousCampaigns?.data?.sms || []
    const previousMms = activePreviousCampaigns?.data?.mms || []
    const savedItems = activeSavedCampaigns?.data?.items || []

    if (previousSms.length > 0) {
      nextOptions.push({
        label: 'PREVIOUSLY SENT SMS',
        options: previousSms.map((item: any) => createOption(item, 'previous-sms')),
      })
    }

    if (previousMms.length > 0) {
      nextOptions.push({
        label: 'PREVIOUSLY SENT MMS',
        options: previousMms.map((item: any) => createOption(item, 'previous-mms')),
      })
    }

    if (savedItems.length > 0) {
      nextOptions.push({
        label: 'SAVED',
        options: savedItems.map((item: any) => createOption(item, 'saved')),
      })
    }

    return nextOptions
  }, [
    hasSearchTerm,
    previousCampaignSearchResults,
    previousCampaigns,
    savedCampaignSearchResults,
    savedCampaigns,
  ])

  useEffect(() => {
    let isMounted = true
    const requestId = requestSequenceRef.current + 1
    requestSequenceRef.current = requestId

    const fetchOptions = async () => {
      setIsLoading(true)
      setSearchError('')

      try {
        const params = {
          limit: 100,
          ...(debouncedSearchTerm?.trim() ? {search: debouncedSearchTerm.trim()} : {}),
        }

        const [savedResult, previousResult] = await Promise.all([
          dispatch(
            GetSavedCampaigsList({
              ...params,
            })
          ),
          dispatch(
            GetPrevioslySentCampaigsList({
              ...params,
              userId: currentUser?._id,
            })
          ),
        ])
        if (!isMounted || requestSequenceRef.current !== requestId || !hasSearchTerm) {
          return
        }

        if (GetSavedCampaigsList.fulfilled.match(savedResult)) {
          setSavedCampaignSearchResults(savedResult.payload)
        }

        if (GetPrevioslySentCampaigsList.fulfilled.match(previousResult)) {
          setPreviousCampaignSearchResults(previousResult.payload)
        }
      } catch (error) {
        if (isMounted && requestSequenceRef.current === requestId) {
          setSearchError('Unable to load campaigns')
          setSavedCampaignSearchResults({})
          setPreviousCampaignSearchResults({})
        }
      } finally {
        if (isMounted && requestSequenceRef.current === requestId) {
          setIsLoading(false)
        }
      }
    }

    fetchOptions()

    return () => {
      isMounted = false
    }
  }, [currentUser?._id, debouncedSearchTerm, dispatch, hasSearchTerm])

  return (
    <Select
      value={value || null}
      inputValue={searchTerm}
      className={className}
      placeholder={placeholder}
      options={options}
      isLoading={isLoading}
      filterOption={null}
      onChange={onChange}
      onBlur={resetSearchTerm}
      onMenuClose={resetSearchTerm}
      onInputChange={(inputValue: string, meta: any) => {
        if (meta.action === 'input-change') {
          setSearchTerm(inputValue)
        } else if (meta.action === 'menu-close' || meta.action === 'input-blur') {
          resetSearchTerm()
        }

        return meta.action === 'input-change' ? inputValue : ''
      }}
      noOptionsMessage={() => {
        if (searchError) {
          return searchError
        }

        return debouncedSearchTerm?.trim() ? 'No matching campaigns found' : 'No campaigns found'
      }}
    />
  )
}

export default CampaignSearchSelect